/**
 * Hydrates product card swatches on visibility to avoid server-side file_img_url costs.
 * Usage:
 * 1. Add [data-swatch-file="filename.jpg"] to the swatch container.
 * 2. Add [data-swatch-image] placeholder inside.
 */

const observer = new IntersectionObserver((entries) => {
  entries.forEach(entry => {
    if (entry.isIntersecting) {
      hydrateSwatches(entry.target);
      observer.unobserve(entry.target);
    }
  });
}, { rootMargin: '200px' });

/**
 * Hydrate all swatches within a container
 * @param {HTMLElement} container 
 */
function hydrateSwatches(container) {
  const swatches = container.querySelectorAll('[data-swatch-file]');
  
  if (swatches.length === 0) {
    return;
  }

  swatches.forEach(swatch => {
    const filename = swatch.dataset.swatchFile;
    const placeholder = swatch.querySelector('[data-swatch-image]');
    
    // If already hydrated or missing data, skip
    if (!filename || !placeholder) return;

    // Use /cdn/shop/files/ proxy for consistent access
    const cdnUrl = `/cdn/shop/files/${filename}?width=64&height=64&crop=center`;
    
    // Create image element but set src LAST
    const img = document.createElement('img');
    img.alt = swatch.title || filename;
    img.width = 32;
    img.height = 32;
    img.className = 'product-swatch__image opacity-0 transition-opacity duration-300';
    
    // Attach handlers BEFORE setting src to catch cached loads
    img.onload = () => {
      // console.log('Swatch loaded:', filename);
      img.classList.remove('opacity-0');
    };
    
    img.onerror = () => {
      console.warn('Swatch failed to load:', cdnUrl);
      img.remove();
    };

    // Set src last
    img.src = cdnUrl;

    // Failsafe for instant cache hits (though onload usually fires if attached before src)
    if (img.complete && img.naturalHeight !== 0) {
      img.onload();
    }
    
    // Inject immediately so browser starts loading
    if (placeholder.parentNode) {
      placeholder.replaceWith(img);
    }
  });
}

/**
 * Initialize observer on specific container or all product cards
 */
export function observeProductCards(container = document) {
  const cards = container.querySelectorAll('[data-product-card]');
  cards.forEach(card => observer.observe(card));
}

/**
 * Initialize observer on product cards
 */
export function initSwatchHydration() {
  observeProductCards();
}

// Auto-initialize
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initSwatchHydration);
} else {
  initSwatchHydration();
}
